import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { books } from '../data/booksData';
import BookActionButtons from './library/BookActionButtons';
import { FaArrowRight, FaBook, FaRegStar, FaStar, FaStarHalfAlt } from 'react-icons/fa';
import { helperFunction } from '../helpers/api_call_helper';
import { getBooks } from '../helpers/backend_helper';
import { normalizeBook } from '../helpers/utils';

export default function FeaturedBooks({ preview = false }) {
    const navigate = useNavigate();
    const [apiBooks, setApiBooks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasLoadedBooks, setHasLoadedBooks] = useState(false);
    const [fetchError, setFetchError] = useState('');

    useEffect(() => {
        let mounted = true;
        setHasLoadedBooks(false);

        helperFunction(
            async () => {
                const response = await getBooks({ type: 'pdf', limit: preview ? 3 : 100 });
                const nextBooks = (response?.data?.data || []).map(normalizeBook);
                if (mounted) {
                    setApiBooks(nextBooks);
                    setHasLoadedBooks(true);
                }
                return response;
            },
            setLoading,
            undefined,
            (message) => {
                if (mounted) {
                    setFetchError(message);
                    setHasLoadedBooks(true);
                }
            },
        );

        return () => {
            mounted = false;
        };
    }, [preview]);

    const fallbackBooks = Object.values(books).map(normalizeBook);
    const displayedBooks = apiBooks.length ? apiBooks : !hasLoadedBooks || loading ? [] : fallbackBooks;
    const previewBooks = displayedBooks.slice(0, 3);
    const englishBooks = displayedBooks.filter(book => book.language === 'English');
    const urduBooks = displayedBooks.filter(book => book.language === 'Urdu');

    const renderRatingStars = (rating = 0) => {
        const rounded = Math.round(Math.min(Math.max(Number(rating) || 0, 0), 5) * 2) / 2;

        return Array.from({ length: 5 }, (_, index) => {
            const position = index + 1;
            if (rounded >= position) return <FaStar key={position} />;
            if (rounded >= position - 0.5) return <FaStarHalfAlt key={position} />;
            return <FaRegStar key={position} />;
        });
    };

    const renderBookCard = (book, idx) => {
        const detailPath = `/book/${book.slug || book.id}`;

        return (
            <div
                key={book.id}
                className="book-card reveal in-view"
                style={{ transitionDelay: `${idx * 0.1}s` }}
                onClick={() => navigate(detailPath)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate(detailPath)}
                aria-label={`View details for ${book.title}`}
            >
                <div className="book-cover">
                    {book.id === 1 && <span className="book-badge">Bestseller</span>}
                    {book.id === 2 && <span className="book-badge">New Release</span>}

                    {book.cover && (book.cover.startsWith('data:') || book.cover.startsWith('http') || book.cover.length > 5) ? (
                        <img
                            src={book.cover}
                            alt={book.imageAlt || book.title}
                            loading="lazy"
                            decoding="async"
                            width="180"
                            height="240"
                        />
                    ) : (
                        <span style={{ fontSize: '5rem', lineHeight: 1, opacity: 0.5 }}><FaBook /></span>
                    )}

                    {/* Hover overlay */}
                    <div className="book-cover-overlay">
                        {/* Bookmark icon — top right */}
                        <div className="absolute top-3 right-3" onClick={(e) => e.stopPropagation()}>
                            <BookActionButtons book={book} variant="card" />
                        </div>
                        <button
                            className="book-quick-view"
                            onClick={(e) => { e.stopPropagation(); navigate(detailPath); }}
                        >
                            Quick View
                        </button>
                    </div>
                </div>

                <div className="book-info">
                    <div className="book-category" style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{book.category}</span>
                        <span style={{ 
                            backgroundColor: 'rgba(201,169,98,0.1)', 
                            color: 'var(--gold)', 
                            padding: '2px 8px', 
                            borderRadius: '12px', 
                            fontSize: '0.8rem',
                            fontWeight: 'bold'
                        }}>
                            {book.language}
                        </span>
                    </div>
                    <h3 className="book-title">{book.title}</h3>
                    <p className="book-author">by {book.author}</p>

                    <div className="book-rating">
                        <span className="book-stars" aria-label={`${book.rating} out of 5 stars`}>
                            {renderRatingStars(book.rating)}
                        </span>
                        <span className="book-reviews">{book.rating.toFixed(1)} ({book.reviews?.toLocaleString() || '0'})</span>
                    </div>

                    <div className="book-price">
                        <span className="price">Rs. {book.printPrice.toLocaleString()}</span>
                        <button
                            className="view-details"
                            onClick={(e) => { e.stopPropagation(); navigate(`/book/${book.id}`); }}
                        >
                            View Details
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderBookSkeletonCards = () => (
        Array.from({ length: 3 }).map((_, index) => (
            <div className="book-card reveal in-view detail-skeleton" key={`book-skeleton-${index}`}>
                <div className="book-cover detail-skeleton-block" />
                <div className="book-info detail-skeleton-stack">
                    <div className="detail-skeleton-line detail-skeleton-label" />
                    <div className="detail-skeleton-line detail-skeleton-heading" />
                    <div className="detail-skeleton-line detail-skeleton-text-sm" />
                    <div className="detail-skeleton-row">
                        <div className="detail-skeleton-line detail-skeleton-pill" />
                        <div className="detail-skeleton-line detail-skeleton-short" />
                    </div>
                    <div className="detail-skeleton-line detail-skeleton-button" />
                </div>
            </div>
        ))
    );

    const renderBookGrid = (items, emptyText) => (
        <div className="content-preview-grid books-preview-grid">
            {(!hasLoadedBooks || loading) && renderBookSkeletonCards()}
            {hasLoadedBooks && !loading && !items.length && (
                <div className="book-card reveal in-view book-info" style={{ color: 'var(--text-dim)' }}>{emptyText}</div>
            )}
            {items.map((book, idx) => renderBookCard(book, idx))}
        </div>
    );

    if (preview) {
        return (
            <section className="section home-preview-section" id="books">
                <div className="section-header-with-nav reveal in-view">
                    <div className="shwn-text">
                        <div className="section-label">Featured Collection</div>
                        <h2 className="section-title">Books by Dolat Khan Kakar</h2>
                        <p className="section-subtitle">
                            A focused selection from the English and Urdu library.
                        </p>
                    </div>
                    <button className="preview-more-btn" type="button" onClick={() => navigate('/books')}>
                        More Books <FaArrowRight />
                    </button>
                </div>

                {renderBookGrid(previewBooks, 'No books available yet.')}

                {fetchError && !apiBooks.length && (
                    <p className="text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                        Showing saved books while the live library loads.
                    </p>
                )}
            </section>
        );
    }

    return (
        <div id="books">
            {/* English Books Section */}
            <section className="section full-list-section" style={{ paddingBottom: '0' }}>
                <div className="section-header-with-nav reveal in-view">
                    <div className="shwn-text">
                        <div className="section-label">Featured Collection</div>
                        <h2 className="section-title">English Books by Dolat Khan Kakar</h2>
                        <p className="section-subtitle">
                            Discover stories, wisdom, and knowledge from one of Pakistan's finest authors
                        </p>
                    </div>
                </div>

                {renderBookGrid(englishBooks, 'No English books available yet.')}
            </section>

            {/* Urdu Books Section */}
            <section className="section full-list-section" style={{ paddingTop: '2rem' }}>
                <div className="section-header-with-nav reveal in-view">
                    <div className="shwn-text">
                        <div className="section-label">Urdu Collection</div>
                        <h2 className="section-title">Urdu Books by Dolat Khan Kakar</h2>
                        <p className="section-subtitle">
                            Explore deep thoughts and reflections presented in the Urdu language
                        </p>
                    </div>
                </div>

                {renderBookGrid(urduBooks, 'No Urdu books available yet.')}
                {fetchError && !apiBooks.length && (
                    <p className="text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                        Showing saved books while the live library loads.
                    </p>
                )}
            </section>
        </div>
    );
}
